import { match } from 'ts-pattern'

import { CustomerInfoChangeSection } from '../../types'

/**
 * The review modal walks the operator through the customer record, then its
 * billing contacts. Both steps edit a single accumulated customer snapshot that
 * is submitted once on confirm, so stepping is purely a UI concern.
 */
export type ReviewStep = 'customer' | 'contacts'

export const REVIEW_STEPS: ReviewStep[] = ['customer', 'contacts']

export const REVIEW_STEP_TITLES: Record<ReviewStep, string> = {
  customer: 'Customer details',
  contacts: 'Billing contact update'
}

/**
 * Which diff section keys (from `buildCustomerInfoSections`) belong to each step:
 * company details and the billing address are edited on the customer step; the
 * billing contacts on the contacts step.
 */
const STEP_SECTION_KEYS: Record<ReviewStep, string[]> = {
  customer: ['details', 'billingAddress'],
  contacts: ['billingContacts']
}

/**
 * The steps that actually have changes, derived solely from the diffed sections
 * (i.e. from the before/after snapshots). A task that only touches the billing
 * address shows just the customer step and no stepper; one that touches both the
 * customer record and its contacts shows both steps. Falls back to the full flow
 * in the degenerate case where nothing diffed, so the modal is never empty.
 */
export const getActiveReviewSteps = (
  sections: CustomerInfoChangeSection[]
): ReviewStep[] => {
  const changedKeys = new Set(sections.map(section => section.key))
  const activeSteps = REVIEW_STEPS.filter(step =>
    STEP_SECTION_KEYS[step].some(key => changedKeys.has(key))
  )

  return activeSteps.length > 0 ? activeSteps : REVIEW_STEPS
}

/**
 * Label for the confirm action, scoped to what actually changed: a task that
 * only touches the customer record (company details / billing address) reads
 * "Update customer details", one that only touches contacts reads "Update
 * billing contact", and a mixed task falls back to the generic "Apply all
 * changes".
 */
export const getConfirmActionLabel = (steps: ReviewStep[]): string => {
  const onlyStep = steps.length === 1 ? steps[0] : undefined

  return match(onlyStep)
    .with('customer', () => 'Update customer details')
    .with('contacts', () => 'Update billing contact')
    .otherwise(() => 'Apply all changes')
}
